import sodium from "libsodium-wrappers"

import { fetchWithAppCheck, getLastAppCheckToken } from "./fetchWithAppCheck"

const CRYPTO_STORAGE_KEYS = [
  "cryptoConnection",
  "webKeyPairPublic",
  "webKeyPairPrivate",
  "mobileIdentityPub",
  "mobileSignedPrekeyPub",
  "sessionKey",
]

let sodiumReadyPromise = null

const ensureSodium = async () => {
  if (!sodiumReadyPromise) {
    sodiumReadyPromise = sodium.ready
  }
  await sodiumReadyPromise
}

export const clearCryptoState = () => {
  for (const key of CRYPTO_STORAGE_KEYS) {
    localStorage.removeItem(key)
  }
}

const bindCryptoToConnection = (connectionId) => {
  const previous = localStorage.getItem("cryptoConnection")
  if (previous !== connectionId) {
    clearCryptoState()
    localStorage.setItem("cryptoConnection", connectionId)
  }
}

const hasWebKeyPair = () =>
  Boolean(
    localStorage.getItem("webKeyPairPrivate") &&
      localStorage.getItem("webKeyPairPublic")
  )

const NONCE_BYTES_AEAD = 12
const NONCE_BYTES_SECRETBOX = 24

const decryptWithKey = (ciphertext, nonce, key) => {
  if (nonce.length === NONCE_BYTES_AEAD) {
    return sodium.crypto_aead_chacha20poly1305_ietf_decrypt(
      null,
      ciphertext,
      null,
      nonce,
      key
    )
  }
  if (nonce.length === NONCE_BYTES_SECRETBOX) {
    return sodium.crypto_secretbox_open_easy(ciphertext, nonce, key)
  }
  throw new Error(
    `Unexpected nonce length: got ${nonce.length}, expected ${NONCE_BYTES_AEAD} (AEAD) or ${NONCE_BYTES_SECRETBOX} (secretbox)`
  )
}

//When you enter the code on the web - we will get back a kind of server token, the next stage is to get pairing keys
export const respondToCode = async (code, name, company) => {
  try {
    const result = await fetchWithAppCheck(`${import.meta.env.VITE_APP_API}/sendmydetails`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ code, name, company })
    })
    const resultJSON = await result.json()
    // if (resultJSON.error) {
    //   // return {
    //   //   error: {
    //   //     code: "error",
    //   //     message: resultJSON.error
    //   //   }
    //   // }
    //   return resultJSON
    // }

    return resultJSON
  } catch (error) {
    console.error(error)
    return null
  }
}
//We want to establish e2e encryption between the web and mobile app
export const getPairingKeys = async (code) => {
  try {
    await ensureSodium()

    const connectionId = sessionStorage.getItem("connection")
    if (!connectionId) {
      return { error: true, message: "Missing connection id" }
    }
    bindCryptoToConnection(connectionId)

    //First we need the key bundle from the mobile app
    const result = await fetchWithAppCheck(
      `${
        import.meta.env.VITE_APP_API
      }/sendmydetails/${code}/${sessionStorage.getItem("connection")}/bundle`,
      {
        method: "GET"
      }
    )
    const resultJSON = await result.json()
    if (resultJSON.error) {
      console.error(resultJSON.error)
      // return {
      //   error: {
      //     code: "error",
      //     message: resultJSON.error
      //   }
      // }
      return resultJSON
    }
    if (resultJSON?.data?.bundle) {
      localStorage.setItem(
        "mobileIdentityPub",
        resultJSON.data.bundle.identityPublicKey
      )
      localStorage.setItem(
        "mobileSignedPrekeyPub",
        resultJSON.data.bundle.signedPrekeyPublic
      )
      const mobileIdentityPub = sodium.from_base64(
        resultJSON.data.bundle.identityPublicKey
      )
      const mobileSignedPrekeyPub = sodium.from_base64(
        resultJSON.data.bundle.signedPrekeyPublic
      )

      //Do a sig check to make sure the signed prekey is valid
      const isValid = sodium.crypto_sign_verify_detached(
        sodium.from_base64(resultJSON.data.bundle.signedPrekeySignature),
        mobileSignedPrekeyPub,
        mobileIdentityPub
      )
      if (!isValid) {
        return {
          error: true,

          message: "Signed prekey signature invalid"
        }
      }

      // Reuse existing web keys on refresh — regenerating breaks decryption because
      // the mobile app encrypts the session key with the web public key we already sent.
      if (hasWebKeyPair()) {
        return true
      }

      //Generate our own keypair, our the web app will use this to encrypt the session key
      const webEphemeralKeyPair = sodium.crypto_box_keypair()
      const webKeyPairPublic = sodium.to_base64(webEphemeralKeyPair.publicKey)
      const webKeyPairPrivate = sodium.to_base64(webEphemeralKeyPair.privateKey)

      //store webKeyPairPublic and webKeyPairPrivate in local storage
      localStorage.setItem("webKeyPairPublic", webKeyPairPublic)
      localStorage.setItem("webKeyPairPrivate", webKeyPairPrivate)

      //Send our public key to the mobile app
      const sessionKeyResult = await fetchWithAppCheck(
        `${
          import.meta.env.VITE_APP_API
        }/sendmydetails/${code}/${sessionStorage.getItem("connection")}/webkey`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            webkey: webKeyPairPublic
          })
        }
      )
      const sessionKeyResultJSON = await sessionKeyResult.json()

      //we essentially just wait for the mobile app to respond with the session key, see checkForSession
      if (sessionKeyResultJSON.error) {
        return {
          error: true,
          message: "Invalid session key"
        }
      }
      return true
    }

    return resultJSON
  } catch (error) {
    console.error(error)
    return null
  }
}

//We are waiting for the mobile app to tell us it connected, so that we can get the session key that it established
export const checkForSession = async (code) => {
  try {
    await ensureSodium()
  } catch (error) {
    console.error(error)
    return { success: false }
  }

  const connectionId = sessionStorage.getItem("connection")
  if (
    !connectionId ||
    localStorage.getItem("cryptoConnection") !== connectionId ||
    !hasWebKeyPair() ||
    !localStorage.getItem("mobileSignedPrekeyPub")
  ) {
    return { success: false }
  }

  const result = await fetchWithAppCheck(
    `${
      import.meta.env.VITE_APP_API
    }/sendmydetails/${code}/${sessionStorage.getItem("connection")}/sessionKey`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    }
  )
  const resultJSON = await result.json()
  if (resultJSON.data?.sessionKey && resultJSON.data?.sessionKeyNonce) {
    try {
      const encryptedSessionKey = sodium.from_base64(resultJSON.data.sessionKey)
      const sessionNonce = sodium.from_base64(resultJSON.data.sessionKeyNonce)
      const webKeyPrivate = sodium.from_base64(
        localStorage.getItem("webKeyPairPrivate")
      )
      const mobileSignedPrekeyPub = sodium.from_base64(
        localStorage.getItem("mobileSignedPrekeyPub")
      )

      const dh = sodium.crypto_scalarmult(webKeyPrivate, mobileSignedPrekeyPub)
      const encryptionKey = sodium.crypto_generichash(32, dh)

      const sessionKey = decryptWithKey(
        encryptedSessionKey,
        sessionNonce,
        encryptionKey
      )
      localStorage.setItem("sessionKey", sodium.to_base64(sessionKey))
      return {
        success: true
      }
    } catch (error) {
      // Server may return a session key before the mobile encrypts with our webkey,
      // or after a page refresh with mismatched keys — keep polling.
      console.warn("Session key not ready yet:", error.message)
      return { success: false }
    }
  }
  if (resultJSON.error) {
    console.error(resultJSON.error)
    return resultJSON
  }
  return {
    success: false
  }
}

//We've established our connection, now go and get the data
export const getData = async (code) => {
  try {
    await ensureSodium()

    const sessionKeyB64 = localStorage.getItem("sessionKey")
    if (!sessionKeyB64) {
      return { success: false }
    }

    const result = await fetchWithAppCheck(
      `${
        import.meta.env.VITE_APP_API
      }/sendmydetails/${code}/${sessionStorage.getItem("connection")}`,
      {
        method: "GET"
      }
    )
    const resultJSON = await result.json()
    if (resultJSON.error) {
      console.error(resultJSON.error)
      return resultJSON
    }

    if (!resultJSON.data || !resultJSON.data.ciphertext || !resultJSON.data.nonce) {
      return {
        complete: resultJSON?.data?.completed || false,
        data: { vault: null },
        error: resultJSON?.error,
        message: resultJSON?.message
      }
    }
    const sessionKey = sodium.from_base64(sessionKeyB64)
    const ciphertext = sodium.from_base64(resultJSON.data.ciphertext)
    const nonce = sodium.from_base64(resultJSON.data.nonce)
    try {
      const plaintextBuf = decryptWithKey(ciphertext, nonce, sessionKey)
      const decryptedData = sodium.to_string(plaintextBuf)
      const decryptedDataJSON = JSON.parse(decryptedData)
      return {
        complete: resultJSON.data.completed,
        data: { vault: decryptedDataJSON },
        error: resultJSON.error,
        message: resultJSON.message
      }
    } catch (decryptError) {
      console.error("Vault decrypt failed:", decryptError)
      return {
        error: true,
        message: "Could not decrypt data. Try reconnecting with a new code.",
      }
    }
  } catch (error) {
    console.error(error)
    return null
  }
}

//Ask for a download, we don't have a public url for this, so we ask the server to decrypt it and get the file for us.
export const getDownloadUrl = async (code, filename) => {
  if (!filename) {
    console.error("getDownloadUrl: missing filename")
    return null
  }
  try {
    const result = await fetchWithAppCheck(
      `${
        import.meta.env.VITE_APP_API
      }/sendmydetails/generateDownloadUrl/${code}/${sessionStorage.getItem(
        "connection"
      )}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ filename })
      }
    )

    const blob = await result.blob()
    const url = window.URL.createObjectURL(blob)
    var fileLink = document.createElement("a")
    fileLink.href = url
    fileLink.download = filename.split("/").pop()
    fileLink.click()
    return {}
  } catch (error) {
    console.error(error)
    return null
  }
}

export const cancelRequest = async (code) => {
  try {
    const result = await fetchWithAppCheck(
      `${
        import.meta.env.VITE_APP_API
      }/sendmydetails/${code}/cancel`,
      {
        method: "POST"
      }
    )
    const resultJSON = await result.json()
    return resultJSON
  } catch (error) {
    console.error(error)
    return null
  }
}

// Same call as cancelRequest, but for the browser's unload path: nothing can be
// awaited there, so keepalive is what lets the request outlive the page.
export const cancelRequestOnUnload = (code) => {
  const url = `${import.meta.env.VITE_APP_API}/sendmydetails/${code}/cancel`
  const token = getLastAppCheckToken()
  try {
    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: token ? { "X-Firebase-AppCheck": token } : undefined
    }).catch((error) => console.error(error))
  } catch (error) {
    console.error(error)
  }
}

export const finishRequest = async (code) => {
  try {
    const result = await fetchWithAppCheck(
      `${
        import.meta.env.VITE_APP_API
      }/sendmydetails/${code}/finished`,
      {
        method: "POST"
      }
    )
    const resultJSON = await result.json()
    return resultJSON
  } catch (error) {
    console.error(error)
    return null
  }
}
